import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { Grabber, GrabberHub } from '../input/Grabbers';
import type { HeldPropKind } from '../input/ModeDefinition';
import { cloneModelTemplate, ModelLibrary, type LoadModelOptions } from '../room/ModelLibrary';
import { applyTintedMaterial } from '../room/ModelMaterials';
import { contactPointFor, reachFor } from './DrawingManager';
import { LinkPreviewRenderer } from './LinkPreviewRenderer';
import type { MarkerWidth, Mode, PenKind, PenSelection, ToolMode } from './ToolMode';

/**
 * Which prop, if any, a mode shows in the dominant hand — `draw`/`scan` are what this file has
 * always rendered; `build`/`link` render nothing. The real, structural answer flows from
 * `ModeRegistry` (`input/ModeDefinition.ts`, `input/ModeDefinitions.ts`) — `AuthoringController`
 * wires it in as `heldPropFor` below — but this default lets `ToolPropRenderer` still be
 * constructed with just a `ToolMode`, the same "defaulted so a test needs nothing extra to
 * wire" convention every other optional caller-supplied hook in this project already follows
 * (`PaletteUI.modelTemplateFor`/`nodeCount`, etc.).
 */
function defaultHeldPropFor(mode: Mode): HeldPropKind {
  if (mode === 'draw') {
    return 'pen-or-eraser';
  }

  if (mode === 'scan') {
    return 'scanner';
  }

  return 'none';
}

/**
 * Renders whichever pen or the eraser `ToolMode` currently selects, in the dominant hand, for
 * as long as the mode is `draw` — restoring the visual half of "the mode is what is in your
 * dominant hand" (plan/history/control-modes.md) that M12A dropped along with the ledge. That drop
 * was an over-application of the owner's own verdict: he called the *ledge* — a shelf of
 * markers you walk to — redundant once the dominant stick could cycle tools directly. The pen
 * itself was never the redundant part, and its absence was worse than a legibility regression:
 * `DrawingManager.contactPointFor` kept computing a real contact point every frame with
 * nothing rendered at it, so a user had to guess where their tip was relative to their hand
 * until ink appeared. In the owner's own words, from the session that found it: "How else
 * would I know where the writing point is when I move my hand to the board?"
 *
 * `contactPointFor` stays the single source of truth for *where* the tip is — this class calls
 * that exact function every frame and derives a mesh's *pivot* from its result, rather than the
 * `PenLedge` shape this replaces, where a mesh's own child `TransformNode` was read back out as
 * the contact point and needed a forced `computeWorldMatrix` to avoid returning a frame-stale
 * answer (CLAUDE.md, "An unforced computeWorldMatrix can return last frame's answer" — the bug
 * that shipped once already as ink trailing the pen). Nothing here is ever read back off a
 * mesh: the prop is unparented and top-level, and every frame writes fresh `position`/
 * `rotationQuaternion` values computed from `Grabber` fields `GrabberHub` already refreshed
 * earlier in the same tick — there is no cached, derived transform anywhere in this path for a
 * render id to make stale. `renderedTipPosition` (used by this class's own tests) reads those
 * same plain fields back with a pure quaternion rotation, never `computeWorldMatrix` or any of
 * the Babylon accessors (`.up`, `.forward`, `getAbsolutePosition()`) that would invoke it.
 *
 * **Job 1, three marker sizes.** `marker.glb`/`marker-fat.glb`/`marker-slim.glb` all shipped
 * loaded (M12B/M13) but only `marker.glb` was ever wired — the M13 author read
 * `PenSelection.kind` (`'pen' | 'eraser'`), found no third value, and concluded there was no
 * selector. There already was one, just not on that axis: `ToolMode.markerWidth` (a separate
 * `'fine' | 'medium' | 'fat'` field, driven by the dominant stick's own up/down flick,
 * `AuthoringController.tickToolCycle`) is *width*, independent of which pen colour or the
 * eraser is selected. `buildPen` now reads it and picks the model with
 * `MARKER_MODEL_KEY_BY_WIDTH`; `ensurePenOrEraserMesh` rebuilds on a width change the same way
 * it already rebuilt on a kind change, because a different width is a different model, not a
 * recolour. All three sizes share one grip height by design (`MARKER_NIB_OFFSET`'s own doc) —
 * the nib sits 45mm from the origin on every size — so switching size mid-hold does not move
 * the drawing point at all; `ToolPropRenderer.test.ts`'s "all three marker sizes share one tip
 * position" proves that directly rather than trusting the shared constant.
 *
 * **Job 2, the scanner.** Scan mode had no held prop at all before this — confirmed from a
 * headset ("the default WebXR controller model and no prop"), and consistent with `ScanTool.ts`
 * never touching `ModelLibrary`. The seam question was whether to fold a `'scanner'` value into
 * `PenSelection.kind`/`PenKind`, or drive the scanner straight off `ToolMode` instead.
 * `PenKind` is answered here in favour of `ToolMode`: `PenKind`/`PenSelection` exist to say
 * *which ink* — a colour always rides along, `ToolMode.selection`/`.eraserSelection` both
 * return one — and the scanner has no ink and no colour to select; widening `PenKind` to a
 * value with no matching `color` would either force a fake one on every `PenSelection` or make
 * `color` conditionally meaningless depending on `kind`, the exact "a type that changes what its
 * other fields mean" trap this project avoids elsewhere. So `tick` gained a second, sibling
 * branch reading `toolMode.isScanMode` directly — the same shape `DrawingManager`/`ConnectionDrawer`
 * already use for `toolMode.isDrawMode` (CLAUDE.md, "A sibling's state is read directly") — and
 * this class's own `shownKind`/`ensureScannerMesh` track `'scanner'` as a third possibility
 * alongside `PenKind`, entirely internal to this file; nothing about `ToolMode` or `PenSelection`
 * changed. `ScanTool` still computes its own aim ray from the grabber's pose
 * (`remoteBoardPoint`) for the scan gesture itself — whether the rendered scanner's own
 * `SCANNER_EMITTER_OFFSET` should instead drive that ray is a question for the control-scheme
 * rework (plan/mode-hierarchy.md), not this change.
 *
 * **Job 2 addendum, the resting angle.** Reported from a headset after Job 2 shipped: the
 * scanner sat at the wrong angle in the hand. `buildScanner` now composes `SCANNER_PIVOT_CORRECTION`
 * on top of the working-point-derived rotation `buildTemplatedProp` already applies — see that
 * constant's own doc for why the scanner needed one at all when the markers and eraser never
 * have, and why fixing it could not touch `SCANNER_EMITTER_OFFSET` itself.
 *
 * **Job 1 addendum, the eraser's resting angle.** Reported from a headset separately: the eraser
 * itself sat at the wrong angle in the hand. `buildEraser` now composes `ERASER_PIVOT_CORRECTION`
 * on top of the working-point-derived rotation the same way `buildScanner` does — 45° about the
 * eraser's own local right axis, but the *opposite* sign from the scanner's, because the owner
 * asked for the scanner to pivot backwards and the eraser to pivot forwards. See that constant's
 * own doc for the sign reasoning and why `ERASER_FELT_OFFSET` itself is untouched.
 *
 * **Job 3, the linker and its live style preview.** plan/mode-hierarchy.md named the gap
 * directly: "Link puts nothing [in the dominant hand] either, and that is a gap, not a decision."
 * `buildLinker` follows the identical template-or-primitive shape `buildPen`/`buildEraser`/
 * `buildScanner` already established — `linker.glb` (`LINKER_MODEL_KEY`/`LINKER_MODEL_PATH`) once
 * `preloadModels()` resolves it, `buildLinkerMesh`'s procedural box otherwise, degrading exactly
 * like every other prop this class renders: no "does this file exist" flag anywhere, a missing
 * model just falls back (CLAUDE.md, "the tray's model-status readout keeps one deliberate
 * hand-mirrored list, and it's the exception, not a precedent" — this file has never kept that
 * flag for its own props, and the linker does not start now). The owner is authoring `linker.glb`
 * in Blender while this lands, so `LINKER_MUZZLE_OFFSET` is a placeholder rather than a real
 * handoff number — see that constant's own doc.
 *
 * **Reached through `toolMode.isLinkMode` directly, not through `heldPropFor`/`HeldPropKind`.**
 * Every other prop kind this class renders flows through the injected `heldPropFor` callback,
 * which — for the one real caller, `AuthoringController.ts` — resolves from
 * `ModeRegistry[mode].heldProp` (`input/ModeDefinition.ts`/`ModeDefinitions.ts`), the single
 * source of truth `plan/mode-hierarchy.md` and that module's own header comment both insist on.
 * The linker does not, because `HeldPropKind` (`'none' | 'pen-or-eraser' | 'scanner'`) has no
 * `'linker'` member to add without widening a type this task's own scope excludes
 * (`src/client-web/src/input/` is another agent's file, mid-flight, while this lands) — so `tick`
 * reads `toolMode.isLinkMode` as a direct, sibling condition instead, the same shape this file's
 * own "Job 2" section used for the scanner before `HeldPropKind` existed to carry that decision
 * uniformly. This is a known, temporary duplication of the mode-to-prop decision, not the
 * intended end state: once `HeldPropKind` gains `'linker'` and the `link` `ModeDefinition`'s own
 * `heldProp` is set to it, this branch's condition becomes redundant with what `heldPropFor`
 * already reports and can be folded back into the `kind === 'linker'` shape the other two props
 * use — nothing about `buildLinker`/`ensureLinkerMesh`/`LinkPreviewRenderer` would need to
 * change, only which condition `tick` reads to reach them.
 *
 * **The live style preview never draws its own arrowhead or dash texture.** `LinkPreviewRenderer`
 * (`LinkPreviewRenderer.ts`) owns one private `ConnectionRenderer` — the identical class
 * `RoomView` uses to draw every real edge in the room — and calls its public `render`/`remove`
 * with a synthetic `ConnectionDto` built from `ToolMode.linkLineSelection`/`linkArrowMode`, so the
 * preview can never drift from what a real edge with the same style actually looks like. `tick`
 * feeds it `renderedTipPosition` (the linker's own muzzle, computed the identical way every other
 * prop's tip already is) and `dominant.forward` every frame the linker is shown; leaving Link
 * mode routes through this class's own `hide()`, which tears the preview down through
 * `ConnectionRenderer.remove` — the same disposal path a real edge gets, not a second one this
 * class has to keep in step with it by hand.
 *
 * **Job 3 addendum, the aim direction.** Reported from a headset once the owner's real
 * `linker.glb` landed: "Linker tool needs a 90 degree pivot, the tip is pointing down."
 * `LINKER_MUZZLE_OFFSET` moved from the placeholder +Z axis (chosen only to make
 * `modelRotationCorrection` reduce to identity before a real model existed to check against) to
 * local -Y, the same axis `MARKER_NIB_OFFSET`/`ERASER_FELT_OFFSET` already use — see that
 * constant's own doc for why this single change both fixes the rotation and is the correct
 * re-check of the offset itself, with no separate pivot constant needed the way the scanner and
 * eraser each required one.
 */
// spec: SPEC-076
export class ToolPropRenderer {
  private mesh: Mesh | null = null;
  private material: StandardMaterial | null = null;

  /** `'scanner'`/`'linker'` are not a `PenSelection.kind` — draw mode's pen/eraser are selected
   *  through `ToolMode.selection`, but the scanner is driven straight off `ToolMode.isScanMode`
   *  and the linker off `ToolMode.isLinkMode` (see the class doc's "Job 2"/"Job 3" notes), so this
   *  field's type has to be wider than `PenSelection.kind` to record either. */
  private shownKind: PenKind | 'scanner' | 'linker' | null = null;
  private shownColor: string | null = null;

  /** The marker's own width when a pen is currently shown — `null` for the eraser and the
   *  scanner, neither of which has one. Tracked separately from `shownColor` because a width
   *  change swaps the underlying model (`MARKER_MODEL_KEY_BY_WIDTH`), not merely its material,
   *  so it has to force `ensurePenOrEraserMesh`'s full rebuild path rather than the colour-only
   *  repaint a same-width colour change takes. */
  private shownWidth: MarkerWidth | null = null;

  /**
   * The current mesh's own writing/contact-face point, in its unrotated local build frame —
   * `localTipOffset(kind)` for the procedural fallback (a declared geometric fact about a mesh
   * this code builds itself), or the prop's own declared working point (`MARKER_NIB_OFFSET`,
   * `ERASER_FELT_OFFSET`) for a model-backed prop — a fact about the authored file, not measured
   * off its geometry (see those constants' own doc for why a bounding-box measurement cannot
   * find "the tip" of anything that is not roughly a rod). Recomputed only on a mesh rebuild
   * (`ensureMesh`), not every tick: it depends only on which mesh is currently shown, never on
   * the grabber's live pose.
   */
  private tipOffset: Vector3 = Vector3.Zero();

  /**
   * How the currently-shown mesh's own build frame is remapped onto local Z before
   * `aimRotation` is applied. `PEN_ROTATION_CORRECTION` for the procedural pen, whose tip is at
   * local +Y by construction; **derived from the declared working-point direction** for a
   * model-backed prop, because an authored model's long axis and its sign are facts about the
   * file rather than about this code. The marker's tip is at local -Y, so the procedural constant pointed
   * its *body* along `forward` and buried it in the whiteboard while `positionProp` still pinned
   * the tip correctly — right point, backwards pen. Recomputed only on a mesh rebuild.
   */
  private rotationCorrection: Quaternion = Quaternion.Identity();

  /**
   * Whichever physical hand this class has actually asked `onSetControllerModelVisible` to
   * hide, or null when nothing is currently hidden through it. Mirrors the bookkeeping
   * `XrManager.setDominantControllerModelVisible` itself keeps (`dominantControllerHiddenFor`)
   * so the two stay in step: see `syncControllerVisibility` for why a live handedness flip
   * needs this remembered here too, not just on the other side of the callback.
   */
  private hiddenHand: XRHandedness | null = null;

  /**
   * Forwarded to `XrManager.setDominantControllerModelVisible` by the caller (main.ts) — this
   * class does not depend on XrManager or WebXR at all, the same arm's-length shape
   * `travelSuppressed`/`HandTeleport.isBusy` already use to cross the same seam. `true` means
   * "show whatever this class most recently asked you to hide"; `false` means "hide whatever
   * hand is dominant right now" — exactly `setDominantControllerModelVisible`'s own two calls,
   * forwarded verbatim rather than re-specified here.
   */
  onSetControllerModelVisible: ((visible: boolean) => void) | null = null;

  /**
   * Link mode's own live style preview at the linker's muzzle — see the class doc's "Job 3" note
   * for why this is owned here rather than by a caller outside this file: it needs nothing this
   * class does not already have (`scene`, and every tick `renderedTipPosition`/`dominant.forward`
   * once the linker is shown), and every other prop-specific concern already lives here too.
   * Constructed unconditionally in the constructor body, not lazily, so a fresh session's very
   * first tick in Link mode has somewhere to render into rather than racing a lazy-init check.
   */
  private readonly linkPreview: LinkPreviewRenderer;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly toolMode: ToolMode,
    // Defaulted rather than always constructed by every caller, the same shape
    // AvatarManager.ts's own constructor uses for the identical reason: a test can supply one it
    // has already populated via ModelLibrary.loadFromBytes (ToolPropRenderer.test.ts), while the
    // one real caller (AuthoringController.ts) takes the default and calls preloadModels() on it
    // itself, below.
    private readonly models: ModelLibrary = new ModelLibrary(scene),

    // Structurally, which prop a mode holds is the mode's own to say (plan/mode-hierarchy.md —
    // "mode tracking holding the different tools") — see `defaultHeldPropFor`'s own doc for why
    // this is an injected function, defaulted, rather than a required `ModeRegistry` argument.
    private readonly heldPropFor: (mode: Mode) => HeldPropKind = defaultHeldPropFor,
  ) {
    this.linkPreview = new LinkPreviewRenderer(scene);
  }

  /**
   * Preloads every authored prop model ahead of the first time it could be needed — the same
   * "before first use, not on demand" rule ModelLibrary.preloadAll and
   * AvatarManager.preloadModels already follow (CLAUDE.md, "The splash screen exists so entering
   * VR is instant"): `ensurePenOrEraserMesh`/`ensureScannerMesh` build synchronously, on the
   * first tick draw or scan mode is active, so whatever has not resolved by then falls back to
   * the procedural shape for the rest of the session. Call once at boot, before the first tick —
   * main.ts does, alongside `avatars.preloadModels()`/`view.preloadModels(nodeTypes)`.
   *
   * Six models load here, and this class now renders every one of them: `marker`, `marker-fat`
   * and `marker-slim` back the three `ToolMode.MarkerWidth` steps (`buildPen`,
   * `MARKER_MODEL_KEY_BY_WIDTH`), `eraser` backs the eraser (`buildEraser`), `scanner` backs
   * the held prop scan mode shows while `ToolMode.isScanMode` (`buildScanner`), and `linker`
   * backs the held prop Link mode shows while `ToolMode.isLinkMode` (`buildLinker`) — see the
   * class doc's own account of Job 1/Job 2/Job 3 for why the marker's selector was already there
   * (`ToolMode.markerWidth`) while the scanner's and the linker's each had to be added as a
   * second, ToolMode-driven branch in `tick` rather than folded into `PenSelection`. Unlike the
   * other five, `linker.glb` does not exist on disk yet at the time this lands — `loadFromPath`
   * degrades to `false` exactly like any other missing model (it is a plain fetch failure, not a
   * special case), so this `Promise.all` still resolves and every other prop still preloads
   * normally; the linker itself renders its procedural fallback until the file arrives, then
   * starts rendering it with no code change (CLAUDE.md, "the tray's model-status readout keeps
   * one deliberate hand-mirrored list, and it's the exception, not a precedent" — this class does
   * not keep one for its own props, so there is nothing to update here when the file lands).
   *
   * `{ normalize: false, preserveMaterials: true }` for the same reasons AvatarManager.ts's own
   * avatar-part preloads use them: every one of these is authored at its true, real-world size,
   * not the node catalogue's uniform TARGET_SIZE, and each carries two material slots
   * (`Mat.Prop.Body`, `Mat.Prop.Tint`) that `Mesh.MergeMeshes` would otherwise collapse into one
   * at load time (LoadModelOptions' own doc, ModelLibrary.ts).
   */
  async preloadModels(): Promise<void> {
    const options: LoadModelOptions = { normalize: false, preserveMaterials: true };

    const outcomes = await Promise.all([
      this.models.loadFromPath(MARKER_MODEL_KEY, MARKER_MODEL_PATH, options),
      this.models.loadFromPath(MARKER_FAT_MODEL_KEY, MARKER_FAT_MODEL_PATH, options),
      this.models.loadFromPath(MARKER_SLIM_MODEL_KEY, MARKER_SLIM_MODEL_PATH, options),
      this.models.loadFromPath(ERASER_MODEL_KEY, ERASER_MODEL_PATH, options),
      this.models.loadFromPath(SCANNER_MODEL_KEY, SCANNER_MODEL_PATH, options),
      this.models.loadFromPath(LINKER_MODEL_KEY, LINKER_MODEL_PATH, options),
    ]);

    const loaded = outcomes.filter(Boolean).length;

    console.info(
      `Tool prop preload: ${loaded}/${outcomes.length} prop model(s) resolved; each renders its ` +
        'authored model when its own resolved, and falls back to its procedural shape otherwise.',
    );
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /**
   * Whichever pen/eraser kind and colour is actually built right now, or null when nothing is
   * shown or the scanner is — not necessarily identical to `toolMode.selection` mid-transition
   * (leaving draw mode clears this a tick after `toolMode.mode` itself changes, since this
   * class's own tick is what notices), but always what it settles to. Exposed for tests.
   *
   * Deliberately typed to exclude `'scanner'` rather than widen `PenSelection` to match: the
   * scanner has no ink colour, so there is no honest `PenSelection` to hand back for it — see
   * `isShowingScanner` for the equivalent question on that branch.
   */
  get renderedSelection(): PenSelection | null {
    if (
      this.shownKind === null ||
      this.shownKind === 'scanner' ||
      this.shownKind === 'linker' ||
      this.shownColor === null
    ) {
      return null;
    }

    return { kind: this.shownKind, color: this.shownColor };
  }

  /** True while the scanner is the currently rendered prop — `renderedSelection`'s own
   *  counterpart for the one shown kind it cannot express (no `PenSelection.color` exists for
   *  a scanner). Exposed for tests. */
  get isShowingScanner(): boolean {
    return this.shownKind === 'scanner';
  }

  /** True while the linker is the currently rendered prop — `isShowingScanner`'s own sibling for
   *  Link mode's held prop (see the class doc's "Job 3" note). Exposed for tests. */
  get isShowingLinker(): boolean {
    return this.shownKind === 'linker';
  }

  /** Whether Link mode's own live style preview is currently built — `LinkPreviewRenderer.isShown`,
   *  forwarded rather than duplicated, so a test can observe it without reaching into this class's
   *  private `linkPreview` field. Exposed for tests. */
  get isShowingLinkPreview(): boolean {
    return this.linkPreview.isShown;
  }

  /**
   * World position of the rendered prop's own writing/contact tip, reconstructed from the
   * mesh's *actual* current `position`/`rotationQuaternion` — not from the formula that placed
   * them — so a test comparing this against a fresh `contactPointFor` call is checking that the
   * two independently agree, not that this class agrees with itself. `this.tipOffset` locates
   * the writing end in the mesh's own build frame — declared, either way: a `localTipOffset`/
   * `localScannerTipOffset` for the procedural fallback, or the prop's own declared working
   * point (`MARKER_NIB_OFFSET`, `ERASER_FELT_OFFSET`, `SCANNER_EMITTER_OFFSET`) for a
   * model-backed prop — and `applyRotationQuaternion` is plain
   * quaternion-vector math with no Scene, render id, or
   * cached world matrix involved anywhere — this mesh is never parented, so there is nothing
   * else it could need. Null when nothing is currently shown.
   */
  get renderedTipPosition(): Vector3 | null {
    const mesh = this.mesh;
    const kind = this.shownKind;

    if (!mesh || kind === null) {
      return null;
    }

    return mesh.position.add(
      this.tipOffset.applyRotationQuaternion(mesh.rotationQuaternion ?? Quaternion.Identity()),
    );
  }

  private tick(): void {
    const dominant = this.grabbers.byRole('dominant');

    if (!dominant) {
      this.hide();
      return;
    }

    const kind = this.heldPropFor(this.toolMode.mode);

    if (kind === 'pen-or-eraser') {
      const selection = this.toolMode.selection;

      if (!selection) {
        this.hide();
        return;
      }

      const mesh = this.ensurePenOrEraserMesh(selection, this.toolMode.markerWidth);
      positionProp(
        mesh,
        contactPointFor(dominant, selection.kind),
        dominant.aimRotation,
        this.tipOffset,
        this.rotationCorrection,
      );
      this.syncControllerVisibility(dominant.handedness);
      return;
    }

    if (kind === 'scanner') {
      const mesh = this.ensureScannerMesh();
      positionProp(
        mesh,
        scannerContactPointFor(dominant),
        dominant.aimRotation,
        this.tipOffset,
        this.rotationCorrection,
      );
      this.syncControllerVisibility(dominant.handedness);
      return;
    }

    // Link mode: read directly off ToolMode rather than through `kind` — see the class doc's
    // "Job 3" note for why `heldPropFor`/`HeldPropKind` cannot express `'linker'` yet without
    // editing a file outside this task's scope. Mutually exclusive with the two branches above by
    // construction (`ToolMode.mode` is a single value), so this never fires alongside either.
    if (this.toolMode.isLinkMode) {
      const mesh = this.ensureLinkerMesh();
      positionProp(
        mesh,
        linkerContactPointFor(dominant),
        dominant.aimRotation,
        this.tipOffset,
        this.rotationCorrection,
      );
      this.syncControllerVisibility(dominant.handedness);

      // renderedTipPosition is this same mesh's own tip — the linker's muzzle — computed from
      // the position/rotation positionProp just wrote, two lines above; reused here rather than
      // re-derived so the preview's anchor and the value a test can independently check
      // (`renderedTipPosition`) can never disagree about where the muzzle actually is. Never
      // null here: mesh/tipOffset were just set by ensureLinkerMesh, and this.shownKind is
      // 'linker' by construction at this point.
      const muzzle = this.renderedTipPosition!;
      this.linkPreview.render(
        muzzle,
        dominant.forward,
        this.toolMode.linkLineSelection,
        this.toolMode.linkArrowMode,
      );
      return;
    }

    this.hide();
  }

  private hide(): void {
    this.disposeMesh();
    this.syncControllerVisibility(null);
    this.linkPreview.hide();
  }

  private disposeMesh(): void {
    // Read off the mesh's own live material, not the `this.material` field — for a model-backed
    // marker whose tint slot resolved, `this.material` is only the tint sub-material (the knob
    // ensurePenOrEraserMesh's colour-change fast path turns), while `mesh.material` is the whole
    // per-instance MultiMaterial that owns it and every other cloned slot alongside it. Disposing
    // `this.material` alone would free the tint slot but leak the MultiMaterial wrapper and every
    // other slot it clones (CLAUDE.md, "That same cascade does not free materials" — the same
    // forceDisposeChildren shape AvatarManager.dispose already uses for exactly this reason).
    const meshMaterial = this.mesh?.material;

    if (meshMaterial instanceof MultiMaterial) {
      meshMaterial.dispose(false, false, true);
    } else {
      meshMaterial?.dispose();
    }

    this.mesh?.dispose();
    this.mesh = null;
    this.material = null;
    this.shownKind = null;
    this.shownColor = null;
    this.shownWidth = null;
    this.tipOffset = Vector3.Zero();
    this.rotationCorrection = Quaternion.Identity();
  }

  /**
   * Builds a fresh mesh on a kind change (pen <-> eraser) or, for the pen, a width change
   * (`marker-fine.glb`/`marker.glb`/`marker-fat.glb` are three different models, not one model
   * scaled three ways) — a colour change on the same kind and width (cycling the wheel through
   * pen colours) repaints the existing material in place instead, since the wheel spins live and
   * a full dispose/recreate on every step would be needless churn for what is, visually, the
   * same prop in a different colour.
   */
  private ensurePenOrEraserMesh(selection: PenSelection, width: MarkerWidth): Mesh {
    const widthMatches = selection.kind !== 'pen' || this.shownWidth === width;

    if (this.mesh && this.shownKind === selection.kind && widthMatches) {
      if (this.shownColor !== selection.color && this.material) {
        applyColor(this.material, selection.kind, selection.color);
        this.shownColor = selection.color;
      }

      return this.mesh;
    }

    this.disposeMesh();

    const built =
      selection.kind === 'pen' ? this.buildPen(selection.color, width) : this.buildEraser(selection.color);

    this.mesh = built.mesh;
    this.material = built.material;
    this.tipOffset = built.tipOffset;
    this.rotationCorrection = built.rotationCorrection ?? rotationCorrectionFor(selection.kind);
    this.shownKind = selection.kind;
    this.shownColor = selection.color;
    this.shownWidth = selection.kind === 'pen' ? width : null;

    return built.mesh;
  }

  /**
   * The scanner's mesh: the authored scanner.glb when `preloadModels()` has resolved it, the
   * procedural box otherwise — Job 2's own answer to `ensurePenOrEraserMesh`, kept as a separate
   * method rather than a third branch inside it because the scanner has no colour to repaint and
   * no width to rebuild on, only a single always-shown shape.
   */
  private ensureScannerMesh(): Mesh {
    if (this.mesh && this.shownKind === 'scanner') {
      return this.mesh;
    }

    this.disposeMesh();

    const built = this.buildScanner();

    this.mesh = built.mesh;
    this.material = built.material;
    this.tipOffset = built.tipOffset;
    this.rotationCorrection = built.rotationCorrection ?? Quaternion.Identity();
    this.shownKind = 'scanner';
    this.shownColor = null;
    this.shownWidth = null;

    return built.mesh;
  }

  /**
   * The linker's mesh: the authored linker.glb when `preloadModels()` has resolved it, the
   * procedural box otherwise — Job 3's own answer to `ensurePenOrEraserMesh`/`ensureScannerMesh`,
   * kept as its own method for the identical reason the scanner's is: Link mode has no colour to
   * repaint (no ink wheel) and no width to rebuild on, only a single always-shown shape.
   */
  private ensureLinkerMesh(): Mesh {
    if (this.mesh && this.shownKind === 'linker') {
      return this.mesh;
    }

    this.disposeMesh();

    const built = this.buildLinker();

    this.mesh = built.mesh;
    this.material = built.material;
    this.tipOffset = built.tipOffset;
    this.rotationCorrection = built.rotationCorrection ?? Quaternion.Identity();
    this.shownKind = 'linker';
    this.shownColor = null;
    this.shownWidth = null;

    return built.mesh;
  }

  /**
   * The eraser's mesh: the authored eraser.glb when `preloadModels()` has resolved it, the
   * procedural box otherwise — the pen's own template-or-primitive shape
   * (`buildTemplatedProp`), reused rather than duplicated now that the eraser has a real model
   * behind it too.
   */
  private buildEraser(color: string): BuiltProp {
    // 'tool-prop-eraser-model', not 'tool-prop-eraser': the procedural fallback
    // (buildEraserMesh, below) already owns that name, and the two have to stay distinguishable
    // by name — the same reason the marker's own model clone is 'tool-prop-marker-<width>' while
    // its procedural fallback is 'tool-prop-pen', not the same name for both.
    return this.buildTemplatedProp(
      ERASER_MODEL_KEY,
      'tool-prop-eraser-model',
      ERASER_FELT_OFFSET,
      Color3.FromHexString(color),
      (material) => applyColor(material, 'eraser', color),
      () => buildEraserMesh(this.scene, color),
      localTipOffset('eraser'),
      ERASER_PIVOT_CORRECTION,
    );
  }

  /**
   * The pen's mesh: whichever of the three authored marker sizes `width` selects
   * (`MARKER_MODEL_KEY_BY_WIDTH`) once `preloadModels()` has resolved it, the procedural
   * cylinder otherwise — `buildTemplatedProp`'s other caller. The instance name carries the
   * width (`tool-prop-marker-fine`/`-medium`/`-fat`) so a test can tell the three apart by name
   * rather than by trusting that the right model key was requested — the exact "a model-backed
   * clone that shares a mesh name with its procedural fallback [or a sibling variant] makes
   * tests pass against either implementation" trap this file's own history already found once
   * (the eraser's `tool-prop-eraser-model` vs `tool-prop-eraser` split, above).
   */
  private buildPen(color: string, width: MarkerWidth): BuiltProp {
    return this.buildTemplatedProp(
      MARKER_MODEL_KEY_BY_WIDTH[width],
      `tool-prop-marker-${width}`,
      MARKER_NIB_OFFSET,
      Color3.FromHexString(color),
      (material) => applyColor(material, 'pen', color),
      () => buildPenMesh(this.scene, color),
      localTipOffset('pen'),
    );
  }

  /**
   * The scanner's mesh: `buildTemplatedProp`'s third caller. Its own accent colour is fixed
   * (`SCANNER_ACCENT_COLOR`), not read from `ToolMode` — scan mode has no ink wheel, so there is
   * no selected colour to tint it with.
   */
  private buildScanner(): BuiltProp {
    const accent = Color3.FromHexString(SCANNER_ACCENT_COLOR);

    return this.buildTemplatedProp(
      SCANNER_MODEL_KEY,
      'tool-prop-scanner',
      SCANNER_EMITTER_OFFSET,
      accent,
      (material) => {
        material.diffuseColor = accent;
        material.emissiveColor = Color3.Black();
      },
      () => buildScannerMesh(this.scene, SCANNER_ACCENT_COLOR),
      localScannerTipOffset(),
      SCANNER_PIVOT_CORRECTION,
    );
  }

  /**
   * The linker's mesh: `buildTemplatedProp`'s fourth caller. Its own accent colour is fixed
   * (`LINKER_ACCENT_COLOR`), not read from `ToolMode` — Link mode has no ink wheel either, the
   * same reasoning `buildScanner`'s own doc gives for the scanner. Still no `pivotCorrection`
   * passed (identity, the default), but for a different reason than before: the headset-reported
   * "tip is pointing down" fix landed in `LINKER_MUZZLE_OFFSET` itself, not as a composed pivot —
   * see that constant's own doc for why the linker's problem was the working-point alignment
   * being wrong, not (like the scanner and eraser) a correct alignment needing a cosmetic rotation
   * on top of it. A future, genuinely cosmetic resting-angle report would still add a
   * `LINKER_PIVOT_CORRECTION` here, the same way `SCANNER_PIVOT_CORRECTION`/`ERASER_PIVOT_CORRECTION`
   * were each added only after a real headset session found one, not speculatively.
   */
  private buildLinker(): BuiltProp {
    const accent = Color3.FromHexString(LINKER_ACCENT_COLOR);

    return this.buildTemplatedProp(
      LINKER_MODEL_KEY,
      'tool-prop-linker-model',
      LINKER_MUZZLE_OFFSET,
      accent,
      (material) => {
        material.diffuseColor = accent;
        material.emissiveColor = Color3.Black();
      },
      () => buildLinkerMesh(this.scene, LINKER_ACCENT_COLOR),
      localLinkerTipOffset(),
    );
  }

  /**
   * The shared template-or-primitive shape for every model-backed prop this class renders — the
   * same pattern `AvatarManager`'s own `buildHead`/`buildBody`/`buildHand` already use for an
   * avatar part, generalised here so `buildPen`, `buildEraser`, `buildScanner` and `buildLinker`
   * are four thin callers rather than four copies of the same clone/tint/fallback dance (they
   * used to be two, before the eraser had a model of its own — see `plan/open-items.md`, "The
   * whiteboard marker model is authored and unused", for the pen-only history this replaces).
   *
   * Takes an explicit `tintColor` plus an `applyFallbackColor` callback rather than a
   * `PenKind`/`color` pair the way this method used to: `PenKind` only ever existed here to pick
   * `applyColor`'s pen-vs-eraser emissive branch, and the scanner is neither — see the class
   * doc's own "Job 2" note for why it is not a third `PenKind` value. The caller now decides how
   * its own procedural fallback material is coloured directly.
   *
   * `workingPointOffset` is the prop's own declared working point (`MARKER_NIB_OFFSET`,
   * `ERASER_FELT_OFFSET`, `SCANNER_EMITTER_OFFSET`) — a fact about the authored file, not
   * measured off this particular clone's geometry (see those constants' own doc for why
   * measuring stopped being viable the moment a non-rod-shaped prop needed one too). It is used
   * unconditionally, on both the tint-resolved and fallback-material branches, because it names
   * the same physical point on the mesh either way — only which *material* ends up on the mesh
   * depends on whether the tint slot was found, never where the tip is. `proceduralTipOffset` is
   * the *procedural* fallback's own equivalent, passed in rather than derived here because it
   * depends on that fallback's own geometry (`localTipOffset`/`localScannerTipOffset`), not on
   * `workingPointOffset` at all.
   *
   * `pivotCorrection` is an extra, caller-supplied rotation composed on top of
   * `modelRotationCorrection`'s own working-point-derived alignment — identity by default, so the
   * pen and eraser (whose declared working point genuinely is their long axis, CLAUDE.md's own
   * account of `modelRotationCorrection`) are unaffected. `buildScanner` is the one caller that
   * passes something else: see `SCANNER_PIVOT_CORRECTION`'s own doc for why the scanner needs a
   * correction `modelRotationCorrection` cannot derive on its own. It only ever applies to the
   * template branch, never `proceduralFallback` — the procedural scanner box already builds its
   * depth along local Z (CLAUDE.md, "the eraser needs no correction" reasoning, which the
   * procedural scanner box shares), so it has no resting-angle problem for a pivot to fix.
   *
   * The template branch tints only `PROP_TINT_MATERIAL_NAME` to `tintColor`, via the same shared
   * `applyTintedMaterial` (`room/ModelMaterials.ts`) AvatarManager's own `applyAvatarMaterial`
   * calls — never the whole mesh, and never the template's own material or another instance's.
   * `fallbackMaterial` is only ever assigned to `mesh.material` when the tint slot is missing
   * (`applyTintedMaterial`'s own documented fallback: a prop must never fail to render over a
   * naming mismatch); when the tint slot *is* found, it is disposed immediately rather than left
   * sitting in the scene unreferenced.
   */
  private buildTemplatedProp(
    modelKey: string,
    instanceName: string,
    workingPointOffset: Vector3,
    tintColor: Color3,
    applyFallbackColor: (material: StandardMaterial) => void,
    proceduralFallback: () => { mesh: Mesh; material: StandardMaterial },
    proceduralTipOffset: Vector3,
    pivotCorrection: Quaternion = Quaternion.Identity(),
  ): BuiltProp {
    const template = this.models.templateFor(modelKey);

    if (!template) {
      return { ...proceduralFallback(), tipOffset: proceduralTipOffset };
    }

    const mesh = cloneModelTemplate(template, instanceName);
    mesh.isPickable = false;
    mesh.rotationQuaternion = Quaternion.Identity();

    const fallbackMaterial = new StandardMaterial(`${instanceName}-fallback-material`, this.scene);
    fallbackMaterial.specularColor = Color3.Black();
    applyFallbackColor(fallbackMaterial);

    const tintMaterial = applyTintedMaterial(
      this.scene,
      mesh,
      PROP_TINT_MATERIAL_NAME,
      instanceName,
      tintColor,
      fallbackMaterial,
      () => warnMissingTintSlot(modelKey),
    );

    // pivotCorrection composed *after* the working-point alignment (applied first, innermost) —
    // it adjusts the already-aligned resting pose, which is the frame the owner was looking at
    // when reporting the scanner's angle, not the raw, unaligned model frame.
    const rotationCorrection = pivotCorrection.multiply(modelRotationCorrection(workingPointOffset));

    if (!tintMaterial) {
      // applyTintedMaterial already assigned fallbackMaterial to mesh.material in this branch —
      // it is the recolour target, exactly as the procedural branch's own `material` is.
      return { mesh, material: fallbackMaterial, tipOffset: workingPointOffset, rotationCorrection };
    }

    // The tint slot resolved, so fallbackMaterial was never assigned anywhere — dispose it now
    // rather than leave it unreferenced in the scene for the rest of the session.
    fallbackMaterial.dispose();

    return { mesh, material: tintMaterial, tipOffset: workingPointOffset, rotationCorrection };
  }

  /**
   * Hides or reveals the dominant controller model in step with whether a prop is currently
   * shown, and on which hand — called every tick with "whichever hand should be hidden right
   * now, or null for none," not with a plain boolean, specifically so a live handedness flip
   * can be told apart from "no change."
   *
   * The known trap this exists to avoid: calling `onSetControllerModelVisible(false)` every
   * single tick while a prop stays continuously shown would, the moment handedness flips
   * mid-hold, ask `XrManager` to hide the *new* dominant hand without ever releasing the *old*
   * one — `setDominantControllerModelVisible`'s own bookkeeping remembers only one hidden hand
   * at a time, so the old hand's controller model would leak hidden for the rest of the
   * session. Comparing `dominantHandedness` against `hiddenHand` and only acting *on a change*
   * is what lets this call `true` (release the old hand — resolved by `XrManager` from what it
   * remembers, not from live `handedness.dominant`) before `false` (hide the new one, which by
   * then *is* what `handedness.dominant` names) — the correct pairing, and the reason
   * `Grabber.role` resolving fresh on every read is safe here despite the trap CLAUDE.md warns
   * about elsewhere: the read happens once per tick, not once per hide-call, and a same-hand
   * tick is a no-op that never touches the callback at all.
   */
  private syncControllerVisibility(dominantHandedness: XRHandedness | null): void {
    if (dominantHandedness === this.hiddenHand) {
      return;
    }

    if (this.hiddenHand !== null) {
      this.onSetControllerModelVisible?.(true);
    }

    if (dominantHandedness !== null) {
      this.onSetControllerModelVisible?.(false);
    }

    this.hiddenHand = dominantHandedness;
  }

  /** Drops whatever is shown and releases any hidden controller model. Used on teardown. */
  dispose(): void {
    this.hide();
    this.linkPreview.dispose();
  }
}

/** A real marker's rough diameter — length is `reachFor('pen')`, shared with DrawingManager. */
const PEN_DIAMETER = 0.018;

/** A real eraser's rough footprint — depth is `reachFor('eraser')`, shared with DrawingManager. */
const ERASER_WIDTH = 0.09;
const ERASER_HEIGHT = 0.03;

/** The authored prop models — six `.glb` files under `public/props/`, all opaque,
 *  `doubleSided: false`, Draco compressed, no textures or rig (the asset author's own handoff for
 *  the first five; `linker.glb` is still being authored, see `LINKER_MODEL_KEY`'s own doc).
 *  Keyed and pathed the same way `AvatarManager`'s own model-backed parts are (a caller-chosen
 *  key, not a `NodeTypeDto` one — `ModelLibrary.loadFromPath`'s own doc), since a rendered tool
 *  prop has no catalogue entry either.
 *
 *  `marker`/`marker-fat`/`marker-slim` back the three `MarkerWidth` steps (`buildPen`,
 *  `MARKER_MODEL_KEY_BY_WIDTH`); `eraser`, `scanner` and `linker` each back their own single prop
 *  (`buildEraser`/`buildScanner`/`buildLinker`). */
const MARKER_MODEL_KEY = 'marker';
const MARKER_MODEL_PATH = '/props/marker.glb';
const MARKER_FAT_MODEL_KEY = 'marker-fat';
const MARKER_FAT_MODEL_PATH = '/props/marker-fat.glb';
const MARKER_SLIM_MODEL_KEY = 'marker-slim';
const MARKER_SLIM_MODEL_PATH = '/props/marker-slim.glb';
const ERASER_MODEL_KEY = 'eraser';
const ERASER_MODEL_PATH = '/props/eraser.glb';
const SCANNER_MODEL_KEY = 'scanner';
const SCANNER_MODEL_PATH = '/props/scanner.glb';

/**
 * `linker`'s own key/path pair, following the identical convention as the other five — but
 * `/props/linker.glb` does not exist on disk yet as this lands. That is expected, not a bug to
 * work around: `ModelLibrary.loadFromPath` (`preloadModels`, above) treats a 404 exactly like any
 * other failed fetch, resolving `false` rather than throwing, and `buildTemplatedProp` treats "no
 * template for this key" exactly like any other unresolved model — the procedural fallback
 * (`buildLinkerMesh`) renders instead, with no separate "does linker.glb exist" flag anywhere in
 * this file (CLAUDE.md, "the tray's model-status readout keeps one deliberate hand-mirrored list,
 * and it's the exception, not a precedent" — this class has never kept one for its own props).
 * Once the owner's export lands at this exact path, `preloadModels()`'s existing fetch starts
 * succeeding and `buildLinker` starts cloning it — no code change required, only the file arriving.
 */
const LINKER_MODEL_KEY = 'linker';
const LINKER_MODEL_PATH = '/props/linker.glb';

/** `ToolMode.MarkerWidth` -> the model key `buildPen` clones for it — `fine` is the slim model,
 *  `fat` is the fat one, and `medium` (the default, `ToolMode`'s own starting width) is the
 *  original `marker.glb` M12B already wired, kept as the plain `MARKER_MODEL_KEY` rather than
 *  renamed to something width-shaped so existing content (and this file's own long-standing
 *  `MARKER_MODEL_KEY`/`MARKER_NIB_OFFSET` naming) does not have to change just because two
 *  siblings joined it. */
const MARKER_MODEL_KEY_BY_WIDTH: Readonly<Record<MarkerWidth, string>> = {
  fine: MARKER_SLIM_MODEL_KEY,
  medium: MARKER_MODEL_KEY,
  fat: MARKER_FAT_MODEL_KEY,
};

/**
 * Every model-backed prop's own working point — the nib, the felt, the emitter — in the model's
 * own build frame (glTF space, the frame `cloneModelTemplate`'s clone sits in before
 * `positionProp` ever moves it). **Declared, from the asset author's own handoff, not measured**
 * off the mesh's bounding box the way this file used to for the marker alone
 * (`modelTipOffset`, deleted with this change): a bounding box has no way to find "the tip" of a
 * block-shaped eraser or the base of a trash can, so measuring would land on an arbitrary corner
 * or face centre for anything that is not roughly a rod. A declared constant is also the only
 * way to guarantee the three marker sizes share one drawing point — see `MARKER_NIB_OFFSET`'s
 * own doc.
 *
 * Every one of these values is asset data, not code derived from anything else in this
 * repository, and CLAUDE.md's own standing instruction applies: use them as declared constants,
 * do not re-derive or "sanity check" them against geometry this project cannot see (Draco keeps
 * the real `.glb` files out of the test suite's reach — `ModelLibrary.ts`'s own Draco note).
 */

/**
 * The nib, shared by all three marker sizes — `marker`, `marker-fat`, `marker-slim` are all
 * gripped at the same height by construction, specifically so the drawing point does not move
 * when a user switches size (`ToolMode.markerWidth`'s own dominant-stick flick, wired to this
 * class's rendering in Job 1 — `MARKER_MODEL_KEY_BY_WIDTH`). Origin at the grip; nib 45mm below
 * it. `ToolPropRenderer.test.ts`'s "all three marker sizes share one tip position" proves the
 * shared-offset claim directly, under a real render loop, rather than trusting that three
 * models built to the same spec actually agree.
 */
export const MARKER_NIB_OFFSET = new Vector3(0, -0.045, 0);

/**
 * The eraser's felt, at the grip's own mid-housing origin. Deliberately *not*
 * `MARKER_NIB_OFFSET`: the eraser's grip sits at a different height on purpose — 43mm, not the
 * markers' 45mm — because a 45mm grip would float the hand clear of the eraser's own housing
 * (the asset author's own handoff). Sharing one constant across every prop here would be exactly
 * the kind of coincidental convenience CLAUDE.md warns reads as tidy and is quietly wrong.
 */
export const ERASER_FELT_OFFSET = new Vector3(0, -0.026, 0);

/**
 * The scanner's emitter, at the grip centre. Wired in Job 2 (`buildScanner`), to `tick`'s own
 * `toolMode.isScanMode` branch — so this offset now gets exercised every tick scan mode is
 * active, the same way `MARKER_NIB_OFFSET`/`ERASER_FELT_OFFSET` are for draw mode. What is *not*
 * wired to it: `ScanTool.ts`'s own aim ray still comes from the grabber's raw pose
 * (`remoteBoardPoint`), not from this offset — whether the rendered emitter's own position
 * should drive that ray instead is explicitly out of scope here (the owner's own brief) and left
 * to the control-scheme rework (plan/mode-hierarchy.md). This offset is therefore still flagged
 * by the asset author as one of the properties least likely to have been proven right by
 * anything short of a headset — rendering it does not validate it, only makes it visible.
 *
 * **A trap already found and fixed once, worth restating so it cannot come back:** the asset
 * build log prints this offset in *Blender* axes as `(0.0, -0.062, 0.055)` — a plausible-looking
 * number that is wrong for this project, which needs the value in glTF space (the frame every
 * other offset in this file, and every model this class loads, is already in). The conversion is
 * `gltf_x = blender_x`, `gltf_y = blender_z`, `gltf_z = -blender_y`. Applied by hand to the
 * printed triple: `gltf_x = 0.0`, `gltf_y = 0.055`, `gltf_z = -(-0.062) = 0.062` — the value
 * below, not the one the build log prints. Reading the printed Blender triple straight into this
 * constant would have put the ray on a plausible-looking but wrong axis, and nothing in this
 * project's own test suite (NullEngine cannot rasterize — CLAUDE.md, "When nothing in the
 * project can see a rendered pixel") would have caught it, because nothing here renders this
 * offset at all yet.
 */
export const SCANNER_EMITTER_OFFSET = new Vector3(0, 0.055, 0.062);

/**
 * The linker's own muzzle — where a link is conceptually about to leave from, and where
 * `LinkPreviewRenderer`'s live style stub anchors — in the model's own build frame.
 *
 * **This used to be a placeholder** (`(0, 0, 0.05)`, straight out along local +Z) chosen
 * specifically so `modelRotationCorrection(LINKER_MUZZLE_OFFSET)` reduced to identity, because
 * `linker.glb` did not exist yet and there was no way to check a guessed rotation
 * (`NullEngine` cannot rasterize a pixel — CLAUDE.md, "When nothing in the project can see a
 * rendered pixel"). The owner's real `linker.glb` has since landed, and a headset pass reported
 * exactly the failure mode that placeholder was blind to: "Linker tool needs a 90 degree pivot,
 * the tip is pointing down." That symptom is the identical shape `MARKER_NIB_OFFSET`'s own doc
 * already found once for the marker — a model whose tip sits along local -Y, rendered with a
 * rotation correction that assumed +Z, ends up with its *body* down `forward` and its tip
 * pointing toward the floor once `aimRotation` (whose own local Y tracks world-ish up) is applied
 * with no correction to counter it. Moving this offset to local -Y — the same axis
 * `MARKER_NIB_OFFSET`/`ERASER_FELT_OFFSET` already use for their own tip — fixes both problems in
 * one edit: `modelRotationCorrection` derives *from* this offset's direction (its own doc: "the
 * rotation that puts a model's own declared working-point direction along local Z"), so a -Y
 * offset yields a rotation carrying local -Y onto local Z — a 90° rotation, exactly the pivot the
 * owner asked for — with no separate `LINKER_PIVOT_CORRECTION` constant needed the way the
 * scanner/eraser each required one for their own, unrelated resting-angle problem (those two
 * needed a *cosmetic* correction on top of an already-correct working-point alignment; this was
 * the working-point alignment itself being wrong).
 *
 * **The magnitude (5cm) is unchanged and is still a placeholder**, not a real handoff number —
 * only the *axis* is grounded in the owner's report; the true muzzle distance is still whatever
 * the real `linker.glb` declares, unverified from source alone (`NullEngine` cannot rasterize a
 * pixel to measure it). **Update this the moment a real declared handoff number arrives**, the
 * same way `SCANNER_EMITTER_OFFSET`'s own doc warns about: if that handoff is printed in Blender
 * axes rather than glTF space, the conversion is `gltf_x = blender_x`, `gltf_y = blender_z`,
 * `gltf_z = -blender_y` — the identical trap that offset's doc found once already, worth checking
 * again here rather than assuming this constant's own axes generalise to a real export's. This
 * value only affects the *model-backed* branch (`buildLinker`'s `buildTemplatedProp` call) — the
 * procedural fallback (`buildLinkerMesh`) has its own, independently declared tip
 * (`localLinkerTipOffset`) and is unaffected by whatever this constant is set to.
 */
export const LINKER_MUZZLE_OFFSET = new Vector3(0, -0.05, 0);

/** The exact name of every prop's per-ink/per-instance accent slot, authored in Blender
 *  alongside `Mat.Prop.Body` (the authored white) — the prop equivalent of `AvatarManager`'s
 *  `Mat.Avatar.Tint`. Matched by name, not by primitive index, for the reason the asset handoff
 *  states directly: if anything ever re-sorts or merges primitives, index-based lookup recolours
 *  the wrong half and it reads as a modelling error rather than a loader one.
 *  `applyTintedMaterial` (`room/ModelMaterials.ts`) already matches by name, not index — this
 *  constant is the one place `buildPen`, `buildEraser`, `buildScanner` and `buildLinker` all get
 *  that name from, so it cannot drift between the four callers. */
const PROP_TINT_MATERIAL_NAME = 'Mat.Prop.Tint';

/** Whether the missing-`Mat.Prop.Tint`-slot warning has already been logged for a given model
 *  key this session — mirrors `AvatarManager`'s own `warnedMissingTintKeys`, kept as a separate
 *  set (not shared with it) because the two dedupe over disjoint key spaces (avatar part keys vs.
 *  prop model keys) and have no reason to share bookkeeping just because they share a mechanism. */
const warnedMissingTintKeys = new Set<string>();

/** Logs once per `modelKey` that a model-backed prop has no `Mat.Prop.Tint` sub-material — an
 *  older or hand-edited export. Never thrown: a prop must render regardless, just tinted as a
 *  whole mesh instead of on its own accent slot (`applyTintedMaterial`'s own documented
 *  fallback). */
function warnMissingTintSlot(modelKey: string): void {
  if (warnedMissingTintKeys.has(modelKey)) {
    return;
  }

  warnedMissingTintKeys.add(modelKey);
  console.warn(
    `Prop model "${modelKey}.glb" has no "${PROP_TINT_MATERIAL_NAME}" material slot — it falls ` +
      'back to a single whole-mesh tint instead of a per-slot accent.',
  );
}

/**
 * Remaps a pen's build-time long axis (local Y, `CreateCylinder`'s own convention) onto local
 * Z, the axis `aimRotation` points along `forward` (CLAUDE.md, "Coordinates" and the note on
 * `TransformNode.forward`) — applied *before* `aimRotation` in `positionProp`, the same
 * composition order `PenLedge.followGrabber` used. The eraser needs no correction: `CreateBox`
 * already builds its depth — the axis that has to face `forward` — as local Z.
 */
const PEN_ROTATION_CORRECTION = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 2);

/**
 * What `buildPen`/`buildEraser`/`buildScanner` hand back. `rotationCorrection` is absent for the
 * procedural meshes, whose build frame is this file's own `PEN_ROTATION_CORRECTION` convention
 * (or, for the eraser and the scanner's own procedural box, no correction at all — see
 * `rotationCorrectionFor`); a model-backed prop supplies its own, derived from where its
 * declared working point actually is.
 */
interface BuiltProp {
  mesh: Mesh;
  material: StandardMaterial;
  tipOffset: Vector3;
  rotationCorrection?: Quaternion;
}

/**
 * The rotation that puts a model's *own* declared working-point direction along local Z, the
 * axis `aimRotation` aims down `forward`. Derived rather than declared, unlike the offset itself:
 * which axis a model is long on, and which end of it does the work, are facts about the exported
 * file that a single hand-picked rotation constant cannot capture for every prop at once — the
 * eraser's felt sits along local -Y (`ERASER_FELT_OFFSET`) exactly like the markers' nib, not
 * along local Z the way the procedural box's own `PEN_ROTATION_CORRECTION`-free convention
 * assumes, so this function has to read the direction back out of the offset it is given rather
 * than assume a fixed axis per prop kind.
 *
 * `PEN_ROTATION_CORRECTION` assumes the procedural pen's build frame, tip at local +Y. The
 * authored marker's tip is at local -Y, so that constant aimed its *body* down `forward` and
 * buried it in the whiteboard — while `positionProp` still pinned the tip exactly on the contact
 * point, because it rotates `tipOffset` by the same quaternion. Right point, backwards pen, and
 * a headset caught it in one session.
 *
 * Roll about the prop's own long axis is left arbitrary: `FromUnitVectorsToRef` picks some
 * perpendicular axis for the antiparallel case, and a marker is near enough a solid of
 * revolution for that not to read. A prop with an asymmetric detail — a clip, a flat edge —
 * would need a real roll reference, the same distinction `aimRotationFromForward` already draws
 * between direction and roll (CLAUDE.md).
 */
export function modelRotationCorrection(workingPointOffset: Vector3): Quaternion {
  const direction = workingPointOffset.clone().normalize();
  const correction = Quaternion.Identity();
  Quaternion.FromUnitVectorsToRef(direction, Vector3.Forward(), correction);
  return correction;
}

/**
 * A scanner-specific correction, composed on top of `modelRotationCorrection(SCANNER_EMITTER_OFFSET)`
 * in `buildTemplatedProp` — **not** a change to `SCANNER_EMITTER_OFFSET` itself, which stays
 * exactly as the asset author declared it and exactly where `renderedTipPosition`/`positionProp`
 * still pin the emitter. Reported from a headset: the scanner sits at the wrong angle in the
 * hand; the owner's own fix, in his own words: "Pivot the scanner model 45 degrees backwards,
 * that will line it up properly."
 *
 * `modelRotationCorrection` assumes the vector from a model's origin to its declared working
 * point *is* the axis that should point down `forward` — true for the markers and the eraser,
 * whose nib/felt sits on what is genuinely their long axis (`MARKER_NIB_OFFSET`/
 * `ERASER_FELT_OFFSET`, both straight down local -Y), so aligning "origin-to-tip" with `forward`
 * is exactly the resting pose a held rod-shaped prop wants. `SCANNER_EMITTER_OFFSET`
 * `(0, 0.055, 0.062)` is not that: the scanner is a boxy handheld device, not a rod, and the
 * emitter is a point on its housing roughly 42° off the device's own forward axis, not the axis
 * itself. Feeding that offset through the same alignment tips the *whole device* up by that same
 * ~42° to force the emitter itself onto `forward` — the exact "aims the model along the emitter
 * direction rather than sitting it in the hand" mechanism this fix's own commit message names.
 * `SCANNER_PIVOT_CORRECTION` is the authored correction for that: a fixed 45° rotation about the
 * device's own local right axis (`Vector3.Right()`, the same axis `PEN_ROTATION_CORRECTION`
 * rotates about, for the identical reason — pitch is the only axis either correction needs to
 * touch), applied only in `buildScanner`, never `buildPen`/`buildEraser`, so the markers' and the
 * eraser's own already-correct resting pose is untouched.
 *
 * The sign here pivots the device's nose back down from the emitter-aligned tilt described
 * above, which is this fix's best reading of "backwards" from the report alone — **NullEngine
 * cannot rasterize a pixel to confirm it (CLAUDE.md, "When nothing in the project can see a
 * rendered pixel"), so whether 45° in this direction is actually the number and the sign the
 * owner meant is only confirmable on a headset**, not from this file or its tests. What the tests
 * below do prove: this rotation is applied, it is exactly 45° about the device's own right axis,
 * composed on top of (not instead of) the existing emitter-alignment correction, and it reaches
 * the scanner alone — never a marker, never the eraser.
 */
export const SCANNER_PIVOT_CORRECTION = Quaternion.RotationAxis(Vector3.Right(), -Math.PI / 4);

/**
 * An eraser-specific correction, composed on top of `modelRotationCorrection(ERASER_FELT_OFFSET)`
 * in `buildTemplatedProp` — **not** a change to `ERASER_FELT_OFFSET` itself, which stays exactly
 * as the asset author's handoff declared it (43mm, not the markers' 45mm, "Do not touch" per that
 * same handoff) and exactly where `renderedTipPosition`/`positionProp` still pin the felt's
 * contact point. Reported from a headset, the same session as `SCANNER_PIVOT_CORRECTION` above:
 * the eraser sits at the wrong angle in the hand; the owner's own fix, in his own words: "The
 * eraser needs to be pivoted 45 degrees forward."
 *
 * Unlike the scanner, the eraser's felt genuinely sits on its own long axis
 * (`ERASER_FELT_OFFSET`, straight down local -Y, same as the markers' nib) — so
 * `modelRotationCorrection` is not compensating for a working point off-axis the way it is for
 * the boxy scanner. This is a separate, cosmetic correction to the model's *resting pose* in the
 * hand, on top of an already-correct alignment; it does not touch, and must not touch, where the
 * felt itself lands, which is exactly why `ERASER_FELT_OFFSET` is untouched here.
 *
 * `ERASER_PIVOT_CORRECTION` is a fixed 45° rotation about the eraser's own local right axis
 * (`Vector3.Right()`, the same axis `SCANNER_PIVOT_CORRECTION`/`PEN_ROTATION_CORRECTION` rotate
 * about, for the identical reason — pitch is the only axis any of these three corrections needs
 * to touch), applied only in `buildEraser`, never `buildPen`/`buildScanner`, so the markers' and
 * the scanner's own resting poses are untouched.
 *
 * **The sign is the opposite of the scanner's, because the direction is the opposite.** The owner
 * asked for the scanner to pivot *backwards* (`SCANNER_PIVOT_CORRECTION`'s own `-Math.PI / 4`
 * about local right) and the eraser to pivot *forwards* — so this constant is `+Math.PI / 4`
 * about the identical axis, not a copy of the scanner's value. **NullEngine cannot rasterize a
 * pixel to confirm this is the correct sense of "forward" in this construction (CLAUDE.md, "When
 * nothing in the project can see a rendered pixel"), so whether +45° here actually reads as
 * forward-tilted on a real headset is only confirmable there** — this is this fix's best reading
 * of the report and the scanner fix's own opposite sign, not a hardware-verified direction. What
 * the tests below do prove: this rotation is applied, it is exactly 45° about the eraser's own
 * right axis, composed on top of (not instead of) the existing felt-alignment correction, and it
 * reaches the eraser alone — never a marker, never the scanner.
 */
export const ERASER_PIVOT_CORRECTION = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 4);

function rotationCorrectionFor(kind: PenKind): Quaternion {
  return kind === 'pen' ? PEN_ROTATION_CORRECTION : Quaternion.Identity();
}

/**
 * The writing/contact-face vertex, in the mesh's own unrotated build frame — a geometric fact
 * about `buildPenMesh`/`buildEraserMesh`'s own geometry (the procedural fallback only — a
 * model-backed prop instead uses its declared working point, `MARKER_NIB_OFFSET` or
 * `ERASER_FELT_OFFSET`, above), used to reconstruct where the tip actually is from the mesh's
 * real current transform. Half of `reachFor(kind)` because both meshes are centred at their own
 * local origin: the pivot sits at the middle, and the tip is half the full length/depth out
 * along the axis `rotationCorrectionFor` maps onto local Z.
 */
function localTipOffset(kind: PenKind): Vector3 {
  const half = reachFor(kind) / 2;
  return kind === 'pen' ? new Vector3(0, half, 0) : new Vector3(0, 0, half);
}

/**
 * How far ahead of the aim pose the *procedural* scanner fallback's own tip sits, for a
 * controller or a tracked hand — this class's own visual placement only, matching
 * `SCANNER_REACH`'s reasoning for why it lives here rather than in `DrawingManager.reachFor`:
 * the scanner has no real touch/draw contact point for a shared "how far" to answer. Kept as its
 * own constant rather than reusing `PEN_REACH`/`ERASER_REACH` because it sizes a *different*
 * mesh (`buildScannerMesh`'s own box), not because the number needs to differ for any other
 * reason.
 */
const SCANNER_PROP_LENGTH = 0.1;
const SCANNER_PROP_WIDTH = 0.05;
const SCANNER_PROP_HEIGHT = 0.05;

/**
 * How the *procedural* linker fallback is sized — a small handheld box, the same shape convention
 * `SCANNER_PROP_LENGTH`/`_WIDTH`/`_HEIGHT` use for the scanner's own procedural fallback, and for
 * the identical reason: this is only ever what renders while `linker.glb` has not resolved, so
 * these numbers are a reasonable placeholder silhouette, not a measurement of anything real.
 */
const LINKER_PROP_LENGTH = 0.12;
const LINKER_PROP_WIDTH = 0.035;
const LINKER_PROP_HEIGHT = 0.035;

/**
 * Writes `mesh`'s position and rotation so its geometric tip — `tipOffset` transformed by the
 * same rotation — lands exactly on `contactPoint`. Derived *from* that point, never the reverse:
 * `pivot = contactPoint - rotate(tipOffset)` places the mesh's own local-origin pivot exactly
 * `tipOffset` back from the tip along the mesh's current orientation, for every grabber kind
 * uniformly — including the desktop pointer, whose own `contactPointFor`/`scannerContactPointFor`
 * already fold their "anchor the tip at position, not a reach ahead of aimOrigin" branch into the
 * point handed in, so no branch is needed here at all.
 *
 * Takes `contactPoint` and `aimRotation` directly rather than a `Grabber`/`kind` pair the way
 * this function used to: the pen/eraser branch derives `contactPoint` from
 * `DrawingManager.contactPointFor(grabber, selection.kind)`, but the scanner has no `PenKind` to
 * pass it — see the class doc's own "Job 2" note — so `tick` computes each branch's own contact
 * point (`contactPointFor` or `scannerContactPointFor`) and hands the result in here uniformly.
 *
 * `tipOffset` is `ToolPropRenderer`'s own `this.tipOffset` — the procedural fallback's own
 * declared tip, or the prop's own declared working point for a model-backed one — passed in
 * rather than recomputed here, since which one applies depends on which mesh was actually built.
 */
function positionProp(
  mesh: Mesh,
  contactPoint: Vector3,
  aimRotation: Quaternion,
  tipOffset: Vector3,
  rotationCorrection: Quaternion,
): void {
  (mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(aimRotation.multiply(rotationCorrection));

  mesh.position.copyFrom(contactPoint.subtract(tipOffset.applyRotationQuaternion(mesh.rotationQuaternion)));
}

/**
 * The scanner's own reach — how far ahead of the aim pose its rendered emitter sits, for a
 * controller or a tracked hand; the desktop pointer's own tip is `position`, exactly, matching
 * `contactPointFor`'s identical branch and for the identical reason (`DrawingManager.ts`'s own
 * doc: a reach stacked onto the camera-anchored pointer `aimOrigin` would land the tip
 * centimetres from the eye). Not `DrawingManager.reachFor`: that function specifically answers
 * "how far is the touch/draw contact point," a real distance `TOUCH_ENGAGE_DISTANCE` and its
 * siblings compare against — the scanner has no equivalent physical-contact test, so there is no
 * shared contract for this number to honour beyond this class's own rendering.
 */
const SCANNER_REACH = 0.12;

function scannerContactPointFor(grabber: Grabber): Vector3 {
  if (grabber.kind === 'pointer') {
    return grabber.position.clone();
  }

  return grabber.aimOrigin.add(grabber.forward.scale(SCANNER_REACH));
}

/** The scanner's own declared tip, for the procedural fallback only — half `SCANNER_PROP_LENGTH`
 *  along local Z, the axis `CreateBox`'s own depth already builds along (matching
 *  `buildEraserMesh`'s identical convention, hence no rotation correction either — see
 *  `rotationCorrectionFor`'s own doc for why that is the eraser's reasoning too). */
function localScannerTipOffset(): Vector3 {
  return new Vector3(0, 0, SCANNER_PROP_LENGTH / 2);
}

/** The linker's own reach — `scannerContactPointFor`'s own sibling for Link mode's held prop,
 *  same value as `SCANNER_REACH` (not shared with it — a genuinely independent number that
 *  happens to agree, the identical precedent `SCANNER_ACCENT_COLOR`'s own doc names for
 *  `ScanTool.STROKE_DIAMETER_METRES`), since both are boxy handheld devices anchored the same
 *  "aimOrigin + forward * reach" way for a controller, and `position` exactly for the desktop
 *  pointer — `DrawingManager.contactPointFor`'s own reasoning for why the pointer's `aimOrigin`
 *  (the camera) cannot take a reach stacked onto it. */
const LINKER_REACH = 0.12;

function linkerContactPointFor(grabber: Grabber): Vector3 {
  if (grabber.kind === 'pointer') {
    return grabber.position.clone();
  }

  return grabber.aimOrigin.add(grabber.forward.scale(LINKER_REACH));
}

/** The linker's own declared tip, for the procedural fallback only — `localScannerTipOffset`'s
 *  own sibling, half `LINKER_PROP_LENGTH` along local Z, the axis `CreateBox`'s own depth already
 *  builds along, so no rotation correction is needed for this branch either. */
function localLinkerTipOffset(): Vector3 {
  return new Vector3(0, 0, LINKER_PROP_LENGTH / 2);
}

function applyColor(material: StandardMaterial, kind: PenKind, color: string): void {
  material.diffuseColor = Color3.FromHexString(color);
  material.emissiveColor = kind === 'pen' ? Color3.FromHexString(color).scale(0.25) : Color3.Black();
}

function buildPenMesh(scene: Scene, color: string): { mesh: Mesh; material: StandardMaterial } {
  const mesh = CreateCylinder(
    'tool-prop-pen',
    { height: reachFor('pen'), diameter: PEN_DIAMETER, tessellation: 10 },
    scene,
  );

  mesh.isPickable = false;
  mesh.rotationQuaternion = Quaternion.Identity();

  const material = new StandardMaterial('tool-prop-pen-material', scene);
  material.specularColor = Color3.Black();
  applyColor(material, 'pen', color);
  mesh.material = material;

  return { mesh, material };
}

function buildEraserMesh(scene: Scene, color: string): { mesh: Mesh; material: StandardMaterial } {
  const mesh = CreateBox(
    'tool-prop-eraser',
    { width: ERASER_WIDTH, height: ERASER_HEIGHT, depth: reachFor('eraser') },
    scene,
  );

  mesh.isPickable = false;
  mesh.rotationQuaternion = Quaternion.Identity();

  const material = new StandardMaterial('tool-prop-eraser-material', scene);
  material.specularColor = Color3.Black();
  applyColor(material, 'eraser', color);
  mesh.material = material;

  return { mesh, material };
}

/** The scanner's own fixed accent colour — not user-selectable, since scan mode has no ink wheel
 *  the way draw mode does. Matches `ScanTool.ts`'s own `LINE_COLOR`, the colour the on-board
 *  range line already renders in, so the held prop and the line it drives read as the same tool
 *  — duplicated rather than imported, the identical "an independent number that happens to
 *  agree" precedent `ScanTool.ts`'s own `STROKE_DIAMETER_METRES` sets for exactly this reason
 *  (that file's own doc: "duplicated rather than imported because that file does not export
 *  them and exact visual-weight parity ... is a legibility nice-to-have here, not a correctness
 *  requirement"). */
const SCANNER_ACCENT_COLOR = '#FFD166';

function buildScannerMesh(scene: Scene, color: string): { mesh: Mesh; material: StandardMaterial } {
  const mesh = CreateBox(
    'tool-prop-scanner-procedural',
    { width: SCANNER_PROP_WIDTH, height: SCANNER_PROP_HEIGHT, depth: SCANNER_PROP_LENGTH },
    scene,
  );

  mesh.isPickable = false;
  mesh.rotationQuaternion = Quaternion.Identity();

  const material = new StandardMaterial('tool-prop-scanner-material', scene);
  material.specularColor = Color3.Black();
  material.diffuseColor = Color3.FromHexString(color);
  material.emissiveColor = Color3.Black();
  mesh.material = material;

  return { mesh, material };
}

/** The linker's own fixed accent colour — not user-selectable, since Link mode has no ink wheel
 *  either. Reuses the identical hex `SCANNER_ACCENT_COLOR`/`ConnectionDrawer.PREVIEW_COLOR` both
 *  already use — duplicated rather than imported, the same "an independent number that happens to
 *  agree" precedent those two set (`SCANNER_ACCENT_COLOR`'s own doc): this is the tool-prop-tint
 *  half of "a colourless mode's device is amber," not the connection-style preview colour, which
 *  `LinkPreviewRenderer` reads from `ToolMode`/`TrashCanRenderer.BIN_COLOR` entirely
 *  independently — the prop's own plastic and the edge it is about to draw are two different
 *  things wearing the same accent by design choice, not by a shared constant. */
const LINKER_ACCENT_COLOR = '#FFD166';

function buildLinkerMesh(scene: Scene, color: string): { mesh: Mesh; material: StandardMaterial } {
  const mesh = CreateBox(
    'tool-prop-linker',
    { width: LINKER_PROP_WIDTH, height: LINKER_PROP_HEIGHT, depth: LINKER_PROP_LENGTH },
    scene,
  );

  mesh.isPickable = false;
  mesh.rotationQuaternion = Quaternion.Identity();

  const material = new StandardMaterial('tool-prop-linker-material', scene);
  material.specularColor = Color3.Black();
  material.diffuseColor = Color3.FromHexString(color);
  material.emissiveColor = Color3.Black();
  mesh.material = material;

  return { mesh, material };
}
